import { formatDateTime } from "@/v2-common/utils/utils.date"
import Collapsible from "@/v2-ui/collapsible"
import Icon from "@/v2-ui/icon"
import Badge from "@/v2-backoffice-client/components/badge"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import TenantPrivacyHistorySourceChange from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyHistorySourceChange"
import {
  PRIVACY_CHANGE_KINDS,
  PRIVACY_CHANGE_KIND_STYLES,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.constants"
import type {
  PrivacyChange,
  PrivacyField,
  PrivacySourceChange,
  PrivacyVisibilityType,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyHistoryEventProps = {
  change: PrivacyChange
  fields: PrivacyField[]
  visibilityTypes: PrivacyVisibilityType[]
  /** The newest write opens expanded; the rest start as their summary line. */
  defaultOpen?: boolean
}

// The event's sources by kind of change, in display order, empty kinds
// dropped — a preset apply yields "updated" and "removed", a save just one.
function sourcesByKind(change: PrivacyChange) {
  return PRIVACY_CHANGE_KINDS.map((kind) => ({
    kind,
    sources: change.Sources.filter((source) => source.Kind === kind),
  })).filter((section) => section.sources.length > 0)
}

// One write to the team's policy. Collapsed, it is the line an auditor
// scans: when, how many sources moved which way, why, and who. Open, the
// sources sit under one kind heading each (only when the write mixed kinds
// — the header pill already says so otherwise) on a shared two-column
// grid, so every before → after in the write lines up in one column.
function TenantPrivacyHistoryEvent(props: TenantPrivacyHistoryEventProps) {
  const { change, fields, visibilityTypes, defaultOpen } = props

  const sections = sourcesByKind(change)
  const isMixed = sections.length > 1

  return (
    <li>
      <Collapsible
        defaultOpen={defaultOpen}
        className="group/history rounded-md border border-muted"
      >
        <Collapsible.Trigger className="flex w-full items-center gap-2 rounded-md p-3 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <Icon
            src="angle-down"
            size={10}
            className="shrink-0 text-muted-foreground transition-transform group-data-[state=closed]/history:-rotate-90"
          />
          <span className="shrink-0 text-sm font-medium">
            {formatDateTime(new Date(change.ChangedAt))}
          </span>

          {sections.map((section) => (
            <Badge
              key={section.kind}
              size="tiny"
              variant={PRIVACY_CHANGE_KIND_STYLES[section.kind].variant}
              className="shrink-0"
            >
              {section.sources.length}{" "}
              {PRIVACY_CHANGE_KIND_STYLES[section.kind].label.toLowerCase()}
            </Badge>
          ))}

          {change.Justification && (
            <span className="min-w-0 truncate text-xs italic text-muted-foreground">
              &ldquo;{change.Justification}&rdquo;
            </span>
          )}

          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            {change.ChangedByUser ? (
              formatUserName(
                change.ChangedByUser.FirstName,
                change.ChangedByUser.LastName,
              )
            ) : change.ChangedByUserId != null ? (
              `User #${change.ChangedByUserId}`
            ) : (
              <Badge
                size="tiny"
                variant="outline"
                tooltip="Removals are not attributed: the database procedures record who inserts or updates a policy row, not who deletes it."
              >
                Not recorded
              </Badge>
            )}
          </span>
        </Collapsible.Trigger>

        {/* Layout classes live on the inner div: Radix keeps the closed
            content mounted with `hidden`, and a `flex` utility on that same
            element would out-rank `[hidden]` and leave an empty padded box. */}
        <Collapsible.Content>
          <div className="flex flex-col gap-3 border-t border-muted px-3 pb-3 pt-2 text-xs">
            {sections.map((section) => (
              <section key={section.kind} className="flex flex-col gap-1">
                {isMixed && (
                  <h4 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {PRIVACY_CHANGE_KIND_STYLES[section.kind].label}
                  </h4>
                )}
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1">
                  {section.sources.map((source: PrivacySourceChange) => (
                    <TenantPrivacyHistorySourceChange
                      key={source.ExternalSourceCode}
                      change={source}
                      fields={fields}
                      visibilityTypes={visibilityTypes}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </Collapsible.Content>
      </Collapsible>
    </li>
  )
}

export default TenantPrivacyHistoryEvent
